/** DECIMAL(20,4) allows 16 integer digits; 11 is the widest at which `value * 10_000`
 *  still rounds to the exact ten-thousandths integer P-D7 sums (12 digits does not). */
const MAX_MONEY_INTEGER_DIGITS = 11;

/** Strict: sold-line money is summed, so a malformed value must fail the fetch, not become null. */
export const readMoney = (value: unknown): number => {
    if (typeof value !== 'string' || !/^-?\d+\.\d{4}$/.test(value)) {
        throw new Error('Malformed money value on a sold line');
    }
    const integerDigits = value.replace('-', '').split('.')[0].replace(/^0+(?=\d)/, '');
    if (integerDigits.length > MAX_MONEY_INTEGER_DIGITS) {
        throw new Error('Money value on a sold line exceeds the exact ten-thousandths range');
    }
    return Number(value);
};

export const readOptionalMoney = <K extends string>(
    raw: Record<string, unknown>,
    wireKey: string,
    key: K,
): Partial<Record<K, number>> =>
    raw[wireKey] === undefined ? {} : ({ [key]: readMoney(raw[wireKey]) } as Record<K, number>);

/** A missing root yields empty lists; a scalar or array root is a contract break and throws,
 *  because reading properties off it would report a real agreement as having no sold lines. */
export const readObject = (value: unknown): Record<string, unknown> => {
    if (value === null || value === undefined) return {};
    if (typeof value !== 'object' || Array.isArray(value)) {
        throw new Error('Malformed sold-line payload: root is not an object');
    }
    return value as Record<string, unknown>;
};

/** A missing list yields an empty list; a non-array is a contract break and throws, because
 *  coercing it would silently drop rows. `context` names the payload in the diagnostic. */
export const readArray = (value: unknown, label: string, context: string): Record<string, unknown>[] => {
    if (value === null || value === undefined) return [];
    if (!Array.isArray(value)) {
        throw new Error(`Malformed ${context} payload: ${label} is not an array`);
    }
    return value as Record<string, unknown>[];
};

const MAX_QUANTITY = 4_294_967_295;

/** Strict: these counts and labels are multiplied, summed or rendered, so a malformed value must
 *  fail the fetch. Every one is a JSON integer on the wire; only quantity carries a column cap. */
export const readInteger = (value: unknown, label: string, max = Number.MAX_SAFE_INTEGER): number => {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > max) {
        throw new Error(`Malformed ${label} on a sold line`);
    }
    return value;
};

export const readNullableInteger = (value: unknown, label: string): number | null =>
    value === null || value === undefined ? null : readInteger(value, label);

export const readQuantity = (value: unknown): number => readInteger(value, 'quantity', MAX_QUANTITY);

export const readOptionalBoolean = (value: unknown, label: string): boolean => {
    if (value === undefined) return false;
    if (typeof value !== 'boolean') throw new Error(`Malformed ${label} on a sold line`);
    return value;
};
